import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Tabs as TabsBase,
  Tab as TabBase,
  CodeEditor,
} from '@elementary/common-ui/components';
import usePrevious from '@elementary/common-ui/hooks/usePrevious';
import { trimEmptyLines } from '@elementary/common-ui/utils/common';
import { TabContext, TabPanel as TabPanelBase } from '@mui/lab';
import { Stack, Typography } from '@mui/material';
import Dependencies from 'catalog/components/Dependencies';
import { ErrorBoundary } from 'react-error-boundary';
import {
  AssetData,
  AssetDataResponse,
  TestData,
} from 'shared/hooks/useAssetData/types';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { ASSET } from '@elementary/common-ui/constants/queryKeys';
import { getLatestExecutionQuery } from 'shared/services/api';
import PanelError from 'shared/components/PanelError';
import useSelectedWarehouseType from 'shared/hooks/useSelectedWarehouseType';
import ExecutionHistory from '../ExecutionHistory';
import { SourceType } from '../../types';
import PipelineExecutionInfo from '../Pipeline/PipelineExecutionInfo';
import ModelColumnsTable from './ModelColumnsTable';
import SemanticElementsTable from './SemanticElementsTable';
import ModelFailureDetails from './executionInfo/ModelFailureDetails';
import BiQueriesTabContent from './BiQueriesTabContent';

const EXECUTABLE_ARTIFACT_TYPES = ['model', 'snapshot', 'seed', 'connector'];

enum TabIndex {
  COLUMNS = 0,
  DEPENDENCIES = 1,
  SQL_QUERY = 2,
  EXECUTION_HISTORY = 3,
  BI_QUERIES = 4,
}

const TabPanel = styled(TabPanelBase)`
  padding: 0;
  min-height: 0;
  height: 100%;
`;

const Tabs = styled(TabsBase)`
  height: 48px;
  min-height: 48px !important;
  border-bottom: ${({ theme }) => `1px solid ${theme.palette.common.gray14}`};
  background-color: ${({ theme }) => theme.palette.common.white};
  margin: 24px 0;
`;

const Tab = styled(TabBase)`
  font-size: 12px;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ theme }) => theme.palette.common.black};
  text-transform: uppercase;
`;

const TabContainer = styled(Stack)`
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.palette.common.gray14};
  border-radius: 8px;
  gap: 16px;
  overflow: auto;
  height: 100%;
`;

type Props = {
  asset?: AssetData;
  testData?: TestData;
  enableEditDependencies?: boolean;
};

const CatalogTabs = ({ asset, testData, enableEditDependencies }: Props) => {
  const [currentTab, setCurrentTab] = useState<string>(
    asset ? 'columns' : 'sql-query',
  );
  const prevAsset = usePrevious(asset);

  const handleTabChange = (_event: SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  useEffect(() => {
    if (testData) {
      setCurrentTab('sql-query');
      return;
    }

    if (
      (asset?.artifactType === 'source' || asset?.type === 'SEMANTIC') &&
      currentTab === 'sql-query'
    ) {
      setCurrentTab('columns');
      return;
    }

    if (asset?.type === 'ETL') {
      setCurrentTab('execution-history');
      return;
    }

    if (currentTab === 'bi-queries' && asset?.type !== 'EXPOSURE') {
      setCurrentTab('columns');
      return;
    }

    if (asset && !prevAsset) {
      setCurrentTab('columns');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset, prevAsset, testData]);

  const selectedEnvId = useSelectedEnv();
  const warehouseType = useSelectedWarehouseType();

  // Fetch query separately if it's missing (e.g., when latestExecution came from skipped run in modelRuns)
  const needsQueryFetch =
    asset?.id &&
    asset?.latestExecution &&
    !asset.latestExecution.query &&
    asset.artifactType === 'model';

  const { data: latestExecutionData } = useGraphqlQuery<{
    asset: Pick<AssetDataResponse, 'latestExecution'>;
  }>({
    query: getLatestExecutionQuery,
    queryKey: [selectedEnvId, ASSET, asset?.id, 'latestExecution'],
    variables: { id: asset?.id },
    enabled: !!needsQueryFetch,
  });

  const fetchedQuery = latestExecutionData?.asset.latestExecution?.query;

  const isSemanticParent = asset?.type === 'SEMANTIC' && !asset?.parentId;

  const navTabs = useMemo(() => {
    const tabs = [];
    if (asset && asset.type !== 'ETL') {
      tabs.push(
        {
          label: isSemanticParent ? 'ELEMENTS' : 'COLUMNS',
          key: 'columns',
          index: 0,
        },
        {
          label: 'DEPENDENCIES',
          key: 'dependencies',
          index: 1,
        },
      );
    }

    if (
      asset?.artifactType === 'model' ||
      testData ||
      asset?.latestExecution?.query ||
      fetchedQuery
    ) {
      tabs.push({
        label: asset?.path?.endsWith('py') ? 'CODE' : 'SQL QUERY',
        key: 'sql-query',
        index: 2,
      });
    }

    if (EXECUTABLE_ARTIFACT_TYPES.includes(asset?.artifactType || '')) {
      tabs.push({
        label: 'EXECUTION HISTORY',
        key: 'execution-history',
        index: 3,
      });
    }

    if (asset?.type === 'EXPOSURE') {
      tabs.push({
        label: 'QUERIES',
        key: 'bi-queries',
        index: TabIndex.BI_QUERIES,
      });
    }

    return tabs;
  }, [asset, testData, fetchedQuery, isSemanticParent]);

  const sqlQuery = trimEmptyLines(
    asset?.latestExecution?.query || fetchedQuery || testData?.query || '',
  );

  return (
    <TabContext value={currentTab.toString()}>
      <Tabs value={currentTab} onChange={handleTabChange}>
        {navTabs.map(({ key, label }) => (
          <Tab key={key} label={label} value={key} wrapped />
        ))}
      </Tabs>
      <TabPanel tabIndex={TabIndex.COLUMNS} value="columns">
        <ErrorBoundary
          fallbackRender={({ error }) => (
            <PanelError error={error} page={'CatalogTabs_Columns'} />
          )}
        >
          {isSemanticParent && asset?.id ? (
            <SemanticElementsTable parentId={asset.id} />
          ) : (
            <ModelColumnsTable {...{ asset }} />
          )}
        </ErrorBoundary>
      </TabPanel>

      <TabPanel value="dependencies" tabIndex={TabIndex.DEPENDENCIES}>
        <ErrorBoundary
          fallbackRender={({ error }) => (
            <PanelError error={error} page={'CatalogTabs_Dependencies'} />
          )}
        >
          <Dependencies
            modelId={asset?.id}
            resource={{ type: 'TABLE', name: asset?.fqn }}
            editable={enableEditDependencies}
          />
        </ErrorBoundary>
      </TabPanel>

      <TabPanel value="sql-query" tabIndex={TabIndex.SQL_QUERY}>
        <ErrorBoundary
          fallbackRender={({ error }) => (
            <PanelError error={error} page={'CatalogTabs_SqlQuery'} />
          )}
        >
          {sqlQuery ? (
            <TabContainer>
              <CodeEditor
                value={sqlQuery}
                mode="sql"
                warehouseType={warehouseType}
                readOnly
              />
            </TabContainer>
          ) : (
            <Typography>No SQL query found</Typography>
          )}
        </ErrorBoundary>
      </TabPanel>
      <TabPanel value="execution-history" tabIndex={TabIndex.EXECUTION_HISTORY}>
        <ErrorBoundary
          fallbackRender={({ error }) => (
            <PanelError error={error} page={'CatalogTabs_ExecutionHistory'} />
          )}
        >
          <ExecutionHistory
            DetailsComponent={({ id }) =>
              asset?.type === 'ETL' ? (
                <PipelineExecutionInfo data={{ rootEventId: id }} />
              ) : (
                <ModelFailureDetails id={id} />
              )
            }
            id={asset?.id || ''}
            sourceType={
              asset?.type === 'ETL' ? SourceType.PIPELINE : SourceType.MODEL
            }
          />
        </ErrorBoundary>
      </TabPanel>

      <TabPanel value="bi-queries" tabIndex={TabIndex.BI_QUERIES}>
        <ErrorBoundary
          fallbackRender={({ error }) => (
            <PanelError error={error} page={'CatalogTabs_BiQueries'} />
          )}
        >
          <BiQueriesTabContent queries={asset?.rawQueries} />
        </ErrorBoundary>
      </TabPanel>
    </TabContext>
  );
};

export default CatalogTabs;
